import { Component, OnInit, Input, ChangeDetectorRef, ChangeDetectionStrategy } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { OrgBudgetComponent } from "./org.budget.component";
import { BudgetPlanComponent } from "./budget-plan/budget.plan.component";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { BudgetLabels } from "./budget.labels";

@TypeManagerDecorator("s25-ng-budget-container")
@Component({
    selector: "s25-ng-budget-container",
    template: `
        @if (init) {
            <div class="budget-container">
                <!-- Budget Navigation Tabs -->
                <div class="budget-navigation">
                    <nav class="budget-tabs">
                        <button
                            class="budget-tab"
                            [class.active]="activeTab === 'overview'"
                            (click)="setActiveTab('overview')"
                        >
                            Overview
                        </button>
                        <button
                            class="budget-tab"
                            [class.active]="activeTab === 'plans'"
                            (click)="setActiveTab('plans')"
                        >
                            {{ labels.BUDGETS }}
                        </button>
                    </nav>
                </div>

                <!-- Budget Content Area -->
                <div class="budget-content">
                    @if (activeTab === "overview") {
                        <s25-ng-org-budget
                            [itemId]="orgId"
                            (budgetSelected)="onBudgetSelectedFromOverview($event)"
                        ></s25-ng-org-budget>
                    }

                    @if (activeTab === "plans") {
                        <s25-ng-budget-plan
                            [orgId]="orgId"
                            [budgetId]="selectedBudgetId"
                            (budgetIdChange)="onBudgetIdChangeFromPlans($event)"
                        ></s25-ng-budget-plan>
                    }
                </div>
            </div>
        }
    `,
    styles: `
        .budget-container {
            padding: 1rem;
            max-width: 1400px;
            margin: 0 auto;
        }

        .budget-navigation {
            margin-bottom: 2rem;
            border-bottom: 2px solid var(--color--border);
        }

        .budget-tabs {
            display: flex;
            gap: 0;
            background: var(--color--surface-2-bg);
            border-radius: 8px 8px 0 0;
            overflow: hidden;
        }

        .budget-tab {
            flex: 1;
            padding: 1rem 1.5rem;
            background: var(--color--surface-2-bg);
            border: none;
            border-bottom: 2px solid transparent;
            cursor: pointer;
            font-size: 1rem;
            font-weight: 500;
            color: var(--color--text-muted);
            transition:
                background-color var(--transition-medium) ease,
                color var(--transition-medium) ease,
                border-color var(--transition-medium) ease;
            min-height: 60px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .budget-tab:hover {
            background: var(--color--surface-2-bg-hover);
            color: var(--color--text);
        }

        .budget-tab.active {
            background: var(--color--surface-bg);
            color: var(--cn-blue);
            border-bottom-color: var(--cn-blue);
            font-weight: 600;
        }

        .budget-content {
            background: var(--color--surface-bg);
            border-radius: 0 0 8px 8px;
            box-shadow: 0 2px 8px color-mix(in srgb, var(--color--text) 12%, transparent);
            min-height: 500px;
        }

        @media (max-width: 768px) {
            .budget-container {
                padding: 0.5rem;
            }

            .budget-navigation {
                margin-bottom: 1rem;
            }

            .budget-tab {
                padding: 0.75rem 1rem;
                font-size: 0.9rem;
                min-height: 44px;
            }

            .budget-content {
                min-height: auto;
            }
        }
    `,
    standalone: true,
    imports: [OrgBudgetComponent, BudgetPlanComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [SeOrgSecurityStore],
})
export class BudgetContainerComponent implements OnInit {
    readonly labels = BudgetLabels;

    @Input() orgId: number;

    init = false;
    activeTab: "overview" | "plans" = "overview";
    selectedBudgetId: number | undefined;

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.init = true;
        this.cd.detectChanges();
    }

    setActiveTab(tab: "overview" | "plans"): void {
        this.activeTab = tab;
        this.cd.detectChanges();
    }

    onBudgetSelectedFromOverview(budgetId: number): void {
        this.selectedBudgetId = budgetId;
        this.activeTab = "plans";
        this.cd.detectChanges();
    }

    onBudgetIdChangeFromPlans(budgetId: number | undefined): void {
        this.selectedBudgetId = budgetId;
    }
}
